import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { createDocumentSelection } from '@/components/case-partner/hooks/useDocumentSelections';
import {
  DocumentSelector,
  type SelectionDisabledResult,
} from '@/components/document-selector/DocumentSelector';
import { type FolderTree as FolderTreeType } from '@/hooks/useFolders';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loading } from '@/components/shared';
import { type Document, useDocuments } from '@/hooks/useDocuments';
import { useFolders } from '@/hooks/useFolders';
import {
  collectDocumentGuidsInFolderSubtrees,
  getClientDocumentsSubtreeFolderGuids,
  getClientDocumentsSubtreeFolderIds,
  sanitizeEcaSavedSelection,
} from '@/lib/eca-client-documents';
import type { DocumentSelection } from '@/types';
import {
  collectDocumentGuidsInFolders,
  isDocumentInFolders,
} from '@/utils/document-utils';

interface EarlyCaseAssessmentDocumentSelectorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matterSlug: string;
  /** ECA selection as `file:<file_reference_guid>` / `folder:<folder_guid>` ids. */
  savedSelection: string[];
  onSave: (next: string[]) => void;
  /** Fallback contributing-doc count when documents haven't loaded yet. */
  contributingDocumentCount: number | null;
}

/** ECA file/folder picker on top of the shared `DocumentSelector`. */
export function EarlyCaseAssessmentDocumentSelectorDialog({
  open,
  onOpenChange,
  matterSlug,
  savedSelection,
  onSave,
  contributingDocumentCount,
}: EarlyCaseAssessmentDocumentSelectorDialogProps) {
  const { documents, isLoading: isLoadingDocuments } = useDocuments(matterSlug);
  const { folders, isLoading: isLoadingFolders } = useFolders(matterSlug);
  const isPickerDataLoading = isLoadingDocuments || isLoadingFolders;

  const clientSubtreeFolderIds = useMemo(
    () => getClientDocumentsSubtreeFolderIds(folders),
    [folders],
  );
  const subtreeFolderGuids = useMemo(
    () => getClientDocumentsSubtreeFolderGuids(folders),
    [folders],
  );
  const autoDocGuids = useMemo(
    () => collectDocumentGuidsInFolders(documents, clientSubtreeFolderIds, subtreeFolderGuids),
    [documents, clientSubtreeFolderIds, subtreeFolderGuids],
  );
  const autoDocCount = autoDocGuids.size;
  // Fall back to the last-run contributing count until documents load.
  const effectiveAutoDocCount =
    autoDocCount > 0
      ? autoDocCount
      : contributingDocumentCount != null
        ? Math.max(0, contributingDocumentCount)
        : 0;

  // Draft state keyed by Document.guid / Folder.guid; rebuilt into
  // `file:`/`folder:` ids on save.
  const [draftFiles, setDraftFiles] = useState<Map<string, Document>>(new Map());
  const [draftFolderGuids, setDraftFolderGuids] = useState<Set<string>>(new Set());

  // Hydrate draft state once per open, after picker data has resolved.
  const hydratedForOpenRef = useRef(false);
  useEffect(() => {
    if (!open) {
      hydratedForOpenRef.current = false;
      return;
    }
    if (hydratedForOpenRef.current) return;
    if (isPickerDataLoading) return;
    const sanitized = sanitizeEcaSavedSelection(
      savedSelection,
      subtreeFolderGuids,
      documents,
      clientSubtreeFolderIds,
      folders,
    );
    const files = new Map<string, Document>();
    const folderGuids = new Set<string>();
    for (const id of sanitized) {
      if (id.startsWith('file:')) {
        const fileRef = id.slice('file:'.length);
        const doc = documents.find((d) => d.id === fileRef);
        if (doc) files.set(doc.guid, doc);
      } else if (id.startsWith('folder:')) {
        folderGuids.add(id.slice('folder:'.length));
      }
    }
    setDraftFiles(files);
    setDraftFolderGuids(folderGuids);
    hydratedForOpenRef.current = true;
  }, [
    open,
    isPickerDataLoading,
    savedSelection,
    subtreeFolderGuids,
    documents,
    clientSubtreeFolderIds,
    folders,
  ]);

  const documentSelections = useMemo<Map<string, DocumentSelection>>(() => {
    const map = new Map<string, DocumentSelection>();
    for (const [guid, doc] of draftFiles) {
      map.set(guid, createDocumentSelection(doc));
    }
    return map;
  }, [draftFiles]);

  const toggleFileSelection = useCallback((doc: Document) => {
    setDraftFiles((prev) => {
      const next = new Map(prev);
      if (next.has(doc.guid)) {
        next.delete(doc.guid);
      } else {
        next.set(doc.guid, doc);
      }
      return next;
    });
  }, []);

  const clearAllSelections = useCallback(() => {
    setDraftFiles(new Map());
    setDraftFolderGuids(new Set());
  }, []);

  const toggleFolderSelection = useCallback((folder: FolderTreeType) => {
    setDraftFolderGuids((prev) => {
      const next = new Set(prev);
      if (next.has(folder.guid)) {
        next.delete(folder.guid);
      } else {
        next.add(folder.guid);
      }
      return next;
    });
  }, []);

  // Client Documents are auto-included server-side; hide them from the picker.
  const shouldHideFolder = useCallback(
    (folder: FolderTreeType) => subtreeFolderGuids.has(folder.guid),
    [subtreeFolderGuids],
  );

  const documentPredicate = useCallback(
    (doc: Document) => !isDocumentInFolders(doc, clientSubtreeFolderIds, subtreeFolderGuids),
    [clientSubtreeFolderIds, subtreeFolderGuids],
  );

  // Doc guids implicitly covered by a checked folder — locked in the file table.
  const folderCoveredDocGuids = useMemo(
    () => collectDocumentGuidsInFolderSubtrees(draftFolderGuids, folders, documents),
    [draftFolderGuids, folders, documents],
  );

  // Auto-inclusion and folder coverage take priority over individual picks.
  const isFileSelectionDisabled = useCallback(
    (doc: Document): SelectionDisabledResult => {
      if (doc.is_draft) {
        return {
          disabled: true,
          reason: 'Draft documents can\u2019t be added to the assessment.',
        };
      }
      if (autoDocGuids.has(doc.guid)) {
        return { disabled: true, reason: 'Already included via Client Documents' };
      }
      if (folderCoveredDocGuids.has(doc.guid)) {
        return {
          disabled: true,
          reason: 'Already included via selected folder \u2014 uncheck the folder to remove.',
        };
      }
      return { disabled: false };
    },
    [autoDocGuids, folderCoveredDocGuids],
  );

  const handleSave = useCallback(() => {
    const next: string[] = [];
    for (const doc of draftFiles.values()) {
      next.push(`file:${doc.id}`);
    }
    for (const folderGuid of draftFolderGuids) {
      next.push(`folder:${folderGuid}`);
    }
    const cleaned = sanitizeEcaSavedSelection(
      next,
      subtreeFolderGuids,
      documents,
      clientSubtreeFolderIds,
      folders,
    );
    onSave(cleaned);
    onOpenChange(false);
    toast.success('Selection saved. Early case assessment regeneration started.');
  }, [
    draftFiles,
    draftFolderGuids,
    subtreeFolderGuids,
    documents,
    clientSubtreeFolderIds,
    folders,
    onSave,
    onOpenChange,
  ]);

  // Surface the auto-included Client Documents count for context.
  const topBanner =
    effectiveAutoDocCount > 0 ? (
      <p className="text-sm text-slate-700">
        <span className="font-medium">{effectiveAutoDocCount}</span> document
        {effectiveAutoDocCount === 1 ? '' : 's'} auto-included from Client Documents.
        {' Pick additional files or folders below to expand the assessment scope.'}
      </p>
    ) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] w-[95vw] max-h-[90vh] h-[90vh] p-0 gap-0 flex flex-col">
        <DialogHeader className="px-6 py-4 border-b border-gray-200 shrink-0">
          <DialogTitle className="text-lg font-semibold">Select files or folders</DialogTitle>
          <DialogDescription className="text-sm text-gray-500 mt-1">
            Choose extra documents to add to the early case assessment beyond Client Documents.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-hidden">
          {isPickerDataLoading ? (
            <div className="flex h-full items-center justify-center">
              <Loading />
            </div>
          ) : (
            <DocumentSelector
              matterSlug={matterSlug}
              documentSelections={documentSelections}
              onToggleSelection={toggleFileSelection}
              onClearSelection={clearAllSelections}
              showUploadButton={false}
              onDone={handleSave}
              doneLabel="Save"
              isSelectionDisabled={isFileSelectionDisabled}
              shouldHideFolder={shouldHideFolder}
              selectedFolderGuidsForChecked={draftFolderGuids}
              onFolderToggleSelection={toggleFolderSelection}
              implicitlySelectedDocGuids={folderCoveredDocGuids}
              documentPredicate={documentPredicate}
              topBanner={topBanner}
              className="h-full"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
